import React, { createContext, useContext, useState, useEffect } from "react";
import { useProduct } from "./ProductContext";
import { useInvoice } from "./InvoiceContext";
import { useInventory } from "./InventoryContext";
import { useAuth } from "./AuthContext";
import axiosInstance from "../api/axiosConfig";

const StockIntegrationContext = createContext();

export const StockIntegrationProvider = ({ children }) => {
  const { products, updateProduct } = useProduct();
  const { invoices } = useInvoice();
  const { recordAdjustment, loadStockLevels } = useInventory();
  const { user } = useAuth();

  const [stockMovements, setStockMovements] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Hydrate movements (adjustments + transfers) and alerts (reorder
  // suggestions) from the backend so they survive a refresh.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const unwrap = (r) => (Array.isArray(r?.data) ? r.data : r?.data?.data || []);
    (async () => {
      const [adjRes, trfRes, reorderRes] = await Promise.allSettled([
        axiosInstance.get("/inventory-adjustments"),
        axiosInstance.get("/inventory-transfers"),
        axiosInstance.get("/inventory/reorder-suggestions"),
      ]);
      if (cancelled) return;

      const movements = [];
      if (adjRes.status === "fulfilled") {
        unwrap(adjRes.value)
          .filter((a) => a.status !== "cancelled" && a.status !== "draft")
          .forEach((a) => {
            (a.items || []).forEach((it) => {
              const qty = Number(it.quantity) || 0;
              movements.push({
                id: `adjustment-${a._id}-${it._id || it.productId}`,
                productId: it.productId,
                productName: it.name || "",
                type: qty >= 0 ? "adjustment_increase" : "adjustment_decrease",
                quantity: qty,
                referenceType: "adjustment",
                referenceId: a._id,
                date: a.date,
                notes: a.reason || a.notes || "Manual stock adjustment",
              });
            });
          });
      }
      if (trfRes.status === "fulfilled") {
        unwrap(trfRes.value)
          .filter((t) => t.status !== "cancelled")
          .forEach((t) => {
            (t.items || []).forEach((it) => {
              movements.push({
                id: `transfer-${t._id}-${it._id || it.productId}`,
                productId: it.productId,
                productName: it.name || "",
                type: "transfer",
                quantity: Number(it.quantity) || 0,
                referenceType: "transfer",
                referenceId: t._id,
                date: t.date,
                notes: `Transfer ${t.fromLocation || ""} -> ${t.toLocation || ""}`,
              });
            });
          });
      }
      if (adjRes.status === "fulfilled" || trfRes.status === "fulfilled") {
        setStockMovements((prev) => {
          const ids = new Set(movements.map((m) => m.id));
          return [...movements, ...prev.filter((m) => !ids.has(m.id))];
        });
      }

      if (reorderRes.status === "fulfilled") {
        const alerts = unwrap(reorderRes.value).map((r) => {
          const stock = Number(r.currentQuantity) || 0;
          const out = stock <= 0;
          return {
            id: `alert-${r.productId}`,
            productId: r.productId,
            productName: r.name,
            type: out ? "out_of_stock" : "low_stock",
            currentStock: stock,
            alertLevel: r.reorderLevel,
            message: out
              ? `${r.name} is out of stock`
              : `${r.name} is running low (${stock} remaining)`,
          };
        });
        setStockAlerts((prev) => {
          const ids = new Set(alerts.map((a) => a.productId));
          return [...alerts, ...prev.filter((a) => !ids.has(a.productId))];
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id || user?._id]);

  // Process stock movement when sale is created/updated
  const processSaleStockImpact = async (invoiceData, isUpdate = false) => {
    if (!invoiceData?.items || !Array.isArray(invoiceData.items)) {
      console.warn("Invalid invoice data for stock processing");
      return;
    }

    setIsProcessing(true);
    try {
      const movements = [];

      for (const item of invoiceData.items) {
        if (!item.productId) continue;

        const product = products.find((p) => p.id === item.productId);
        if (!product) continue;

        const movementQuantity = parseFloat(item.quantity) || 0;

        // Update product stock (decrease for sale)
        const currentStock = parseFloat(product.quantity) || 0;
        const newStock = Math.max(0, currentStock - movementQuantity);

        await updateProduct(product.id, {
          quantity: newStock,
          lastSaleDate: new Date().toISOString(),
          lastSaleQuantity: movementQuantity,
        });

        // Record movement
        const movement = {
          id: `sale-${invoiceData.id || invoiceData.number}-${
            item.productId
          }-${Date.now()}`,
          productId: item.productId,
          productName: product.name,
          type: "sale",
          quantity: -movementQuantity,
          previousStock: currentStock,
          newStock: newStock,
          referenceType: "invoice",
          referenceId: invoiceData.id || invoiceData.number,
          date: invoiceData.date || new Date().toISOString(),
          notes: `Sale via Invoice ${
            invoiceData.number || invoiceData.invoiceNumber
          }`,
        };

        movements.push(movement);

        // Check for stock alerts
        const minQuantity = parseFloat(product.minQuantity) || 0;
        const reorderLevel = parseFloat(product.reorderLevel) || 0;
        const alertLevel = Math.max(minQuantity, reorderLevel);

        if (newStock <= alertLevel && newStock > 0) {
          setStockAlerts((prev) => [
            ...prev,
            {
              id: `alert-${product.id}-${Date.now()}`,
              productId: product.id,
              productName: product.name,
              type: "low_stock",
              currentStock: newStock,
              alertLevel: alertLevel,
              message: `${product.name} is running low (${newStock} remaining)`,
            },
          ]);
        } else if (newStock <= 0) {
          setStockAlerts((prev) => [
            ...prev,
            {
              id: `alert-${product.id}-${Date.now()}`,
              productId: product.id,
              productName: product.name,
              type: "out_of_stock",
              currentStock: newStock,
              message: `${product.name} is out of stock`,
            },
          ]);
        }
      }

      setStockMovements((prev) => [...prev, ...movements]);
      await loadStockLevels(); // Refresh inventory data

      return movements;
    } catch (error) {
      console.error("Error processing sale stock impact:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // Process stock movement when purchase is created/completed
  const processPurchaseStockImpact = async (
    purchaseData,
    isReceived = true
  ) => {
    if (!purchaseData?.items || !Array.isArray(purchaseData.items)) {
      console.warn("Invalid purchase data for stock processing");
      return;
    }

    setIsProcessing(true);
    try {
      const movements = [];

      for (const item of purchaseData.items) {
        if (!item.productId) continue;

        const product = products.find((p) => p.id === item.productId);
        if (!product) continue;

        const movementQuantity = parseFloat(item.quantity) || 0;

        // Update product stock (increase for purchase)
        const currentStock = parseFloat(product.quantity) || 0;
        const newStock = currentStock + movementQuantity;

        await updateProduct(product.id, {
          quantity: newStock,
          lastPurchaseDate: new Date().toISOString(),
          lastPurchaseQuantity: movementQuantity,
          lastPurchasePrice: item.price,
        });

        // Record movement
        const movement = {
          id: `purchase-${purchaseData.id || purchaseData.orderNumber}-${
            item.productId
          }-${Date.now()}`,
          productId: item.productId,
          productName: product.name,
          type: "purchase",
          quantity: movementQuantity,
          previousStock: currentStock,
          newStock: newStock,
          referenceType: "purchase_order",
          referenceId: purchaseData.id || purchaseData.orderNumber,
          date: purchaseData.date || new Date().toISOString(),
          notes: `Purchase via PO ${purchaseData.orderNumber}`,
        };

        movements.push(movement);
      }

      setStockMovements((prev) => [...prev, ...movements]);
      await loadStockLevels(); // Refresh inventory data

      return movements;
    } catch (error) {
      console.error("Error processing purchase stock impact:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // Process manual stock adjustment
  const processStockAdjustment = async (adjustmentData) => {
    if (!adjustmentData?.items || !Array.isArray(adjustmentData.items)) {
      console.warn("Invalid adjustment data for stock processing");
      return;
    }

    setIsProcessing(true);
    try {
      const movements = [];

      for (const item of adjustmentData.items) {
        if (!item.productId) continue;

        const product = products.find((p) => p.id === item.productId);
        if (!product) continue;

        const currentStock = parseFloat(product.quantity) || 0;
        const adjustmentQuantity = parseFloat(item.adjustmentQuantity) || 0;
        const newStock = Math.max(0, currentStock + adjustmentQuantity);

        await updateProduct(product.id, {
          quantity: newStock,
          lastAdjustmentDate: new Date().toISOString(),
          lastAdjustmentQuantity: adjustmentQuantity,
        });

        // Record movement
        const movement = {
          id: `adjustment-${adjustmentData.id}-${item.productId}-${Date.now()}`,
          productId: item.productId,
          productName: product.name,
          type:
            adjustmentQuantity > 0
              ? "adjustment_increase"
              : "adjustment_decrease",
          quantity: adjustmentQuantity,
          previousStock: currentStock,
          newStock: newStock,
          referenceType: "adjustment",
          referenceId: adjustmentData.id,
          date: adjustmentData.date || new Date().toISOString(),
          notes: adjustmentData.reason || "Manual stock adjustment",
        };

        movements.push(movement);
      }

      setStockMovements((prev) => [...prev, ...movements]);
      await recordAdjustment(adjustmentData); // Record in inventory system

      return movements;
    } catch (error) {
      console.error("Error processing stock adjustment:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // Get stock status for a product
  const getStockStatus = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return { status: "unknown", message: "Product not found" };

    const currentStock = parseFloat(product.quantity) || 0;
    const minQuantity = parseFloat(product.minQuantity) || 0;
    const reorderLevel = parseFloat(product.reorderLevel) || 0;
    const alertLevel = Math.max(minQuantity, reorderLevel);

    if (currentStock <= 0) {
      return {
        status: "out_of_stock",
        message: "Out of stock",
        color: "danger",
      };
    } else if (currentStock <= alertLevel) {
      return { status: "low_stock", message: "Low stock", color: "warning" };
    } else if (minQuantity > 0 && currentStock > minQuantity * 20) {
      return { status: "overstocked", message: "Overstocked", color: "info" };
    } else {
      return { status: "in_stock", message: "In stock", color: "success" };
    }
  };

  // Get recent stock movements
  const getRecentMovements = (limit = 10) => {
    return [...stockMovements]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, limit);
  };

  // Get products with low stock
  const getLowStockProducts = () => {
    return products.filter((product) => {
      const currentStock = parseFloat(product.quantity) || 0;
      const minQuantity = parseFloat(product.minQuantity) || 0;
      const reorderLevel = parseFloat(product.reorderLevel) || 0;
      const alertLevel = Math.max(minQuantity, reorderLevel);
      return currentStock <= alertLevel && currentStock > 0;
    });
  };

  // Get out of stock products
  const getOutOfStockProducts = () => {
    return products.filter((product) => {
      const currentStock = parseFloat(product.quantity) || 0;
      return currentStock <= 0;
    });
  };

  // Clear old alerts
  const clearAlert = (alertId) => {
    setStockAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  };

  // Get inventory summary with real-time data
  const getInventorySummary = () => {
    const summary = {
      totalProducts: products.length,
      totalStockValue: 0,
      lowStockCount: 0,
      outOfStockCount: 0,
      overstockedCount: 0,
      recentMovements: getRecentMovements(5),
    };

    products.forEach((product) => {
      const currentStock = parseFloat(product.quantity) || 0;
      const costPrice = parseFloat(product.costPrice) || 0;
      const minQuantity = parseFloat(product.minQuantity) || 0;
      const reorderLevel = parseFloat(product.reorderLevel) || 0;
      const alertLevel = Math.max(minQuantity, reorderLevel);

      // Calculate total stock value
      summary.totalStockValue += currentStock * costPrice;

      // Count stock statuses
      if (currentStock <= 0) {
        summary.outOfStockCount++;
      } else if (currentStock <= alertLevel) {
        summary.lowStockCount++;
      } else if (minQuantity > 0 && currentStock > minQuantity * 20) {
        summary.overstockedCount++;
      }
    });

    return summary;
  };

  const value = {
    // Stock processing functions
    processSaleStockImpact,
    processPurchaseStockImpact,
    processStockAdjustment,

    // Stock status and alerts
    getStockStatus,
    stockAlerts,
    clearAlert,

    // Stock movements
    stockMovements,
    getRecentMovements,

    // Stock analysis
    getLowStockProducts,
    getOutOfStockProducts,
    getInventorySummary,

    // Processing state
    isProcessing,
  };

  return (
    <StockIntegrationContext.Provider value={value}>
      {children}
    </StockIntegrationContext.Provider>
  );
};

export const useStockIntegration = () => {
  const context = useContext(StockIntegrationContext);
  if (!context) {
    throw new Error(
      "useStockIntegration must be used within a StockIntegrationProvider"
    );
  }
  return context;
};

export default StockIntegrationContext;
